import React, { useState } from 'react';

/**
 * Reusable POD/completion view modal.
 * Used in RouteReports (and anywhere else) to show completion data without
 * requiring RouteCalendar state.
 */
const RouteCompletionViewModal = ({ completion, route, theme, onClose }) => {
    const [enlargedImg, setEnlargedImg] = useState(null);

    if (!completion) return null;

    const isDark = theme !== 'light';
    const overlay = {
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.75)', zIndex: 9999,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
    };
    const box = {
        background: isDark ? '#222' : '#fff',
        color: isDark ? '#fff' : '#222',
        borderRadius: 10,
        border: '2px solid rgba(34,197,94,0.8)',
        padding: 28,
        minWidth: 520,
        maxWidth: 780,
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        position: 'relative',
    };
    const label = { opacity: 0.6, fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em' };
    const divider = { borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}`, marginBottom: 14, paddingBottom: 14 };

    // Extract signature from the various formats it can arrive in
    let signatureUrl = null;
    try {
        const raw = completion.customer_signature_data;
        if (typeof raw === 'string' && raw.startsWith('{')) {
            signatureUrl = JSON.parse(raw)?.signature;
        } else if (typeof raw === 'string') {
            signatureUrl = raw;
        } else if (raw?.signature) {
            signatureUrl = raw.signature;
        }
    } catch (_) {}

    return (
        <>
            {/* Main modal */}
            <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div style={box} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...divider }}>
                        <h3 style={{ margin: 0, fontSize: '1.2em', color: 'rgba(34,197,94,0.9)' }}>
                            ✓ Job Completion — {completion.route_ref || completion.route_uuid}
                        </h3>
                        <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: isDark ? '#ccc' : '#444' }}>&times;</button>
                    </div>

                    {/* Route info strip */}
                    {route && (
                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14, padding: '10px 14px', borderRadius: 6, background: isDark ? '#2a2a2a' : '#f5f5f5', border: `1px solid ${isDark ? '#3a3a3a' : '#e0e0e0'}` }}>
                            {route.job     && <div><span style={label}>Job</span><br /><strong>{route.job}</strong></div>}
                            {route.date    && <div><span style={label}>Date</span><br /><strong>{route.date}</strong></div>}
                            {route.driver  && <div><span style={label}>Driver</span><br /><strong>{route.driver}</strong></div>}
                            {route.truck   && <div><span style={label}>Truck</span><br /><strong>{route.truck}</strong></div>}
                            {route.route   && <div><span style={label}>Route</span><br /><strong>{route.route}</strong></div>}
                            {route.job_comments && <div style={{ flexBasis: '100%' }}><span style={label}>Job Comments</span><br /><span>{route.job_comments}</span></div>}
                        </div>
                    )}

                    {/* Signed by / dates */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
                        {completion.signed_by   && <div><span style={label}>Signed by</span><br /><strong>{completion.signed_by}</strong></div>}
                        {completion.signed_at   && <div><span style={label}>Signed at</span><br /><strong>{new Date(completion.signed_at).toLocaleString()}</strong></div>}
                        {completion.submitted_at && <div><span style={label}>Submitted</span><br /><strong>{new Date(completion.submitted_at).toLocaleString()}</strong></div>}
                    </div>

                    {/* GPS */}
                    {(completion.geocoded_address || completion.latitude) && (
                        <div style={{ marginBottom: 14 }}>
                            <span style={label}>Location</span><br />
                            {completion.geocoded_address && <div style={{ marginTop: 2 }}>{completion.geocoded_address}</div>}
                            {completion.latitude && completion.longitude && (
                                <a
                                    href={`https://www.google.com/maps?q=${completion.latitude},${completion.longitude}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ fontSize: '0.85em', color: '#007bff' }}
                                >
                                    📍 {completion.latitude.toFixed(5)}, {completion.longitude.toFixed(5)}
                                    {completion.location_accuracy ? ` (±${Math.round(completion.location_accuracy)}m)` : ''}
                                </a>
                            )}
                        </div>
                    )}

                    {/* Notes */}
                    {completion.completion_notes && (
                        <div style={{ marginBottom: 14 }}>
                            <span style={label}>Driver Notes</span><br />
                            <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{completion.completion_notes}</div>
                        </div>
                    )}

                    {completion.driver_comments && (
                        <div style={{ marginBottom: 14 }}>
                            <span style={label}>Driver Comments</span><br />
                            <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{completion.driver_comments}</div>
                        </div>
                    )}

                    {/* Customer Signature */}
                    <div style={{ marginBottom: 14 }}>
                        <span style={label}>Customer Signature</span><br />
                        {signatureUrl ? (
                            <img
                                src={signatureUrl}
                                alt="Customer signature"
                                style={{ maxWidth: '100%', maxHeight: 140, border: `1px solid ${isDark ? '#555' : '#ccc'}`, borderRadius: 6, marginTop: 6, background: '#fff', cursor: 'pointer' }}
                                onClick={() => setEnlargedImg({ url: signatureUrl, title: `Signature — ${completion.signed_by || ''}` })}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            <em style={{ opacity: 0.5 }}>No signature recorded</em>
                        )}
                    </div>

                    {/* Delivery photos */}
                    {completion.delivery_images?.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <span style={label}>Delivery Photos ({completion.delivery_images.length})</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                {completion.delivery_images.map((url, i) => (
                                    <img key={i} src={url} alt={`Delivery ${i + 1}`}
                                        style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: `1px solid ${isDark ? '#555' : '#ccc'}`, cursor: 'pointer' }}
                                        onClick={() => setEnlargedImg({ url, title: `Delivery Photo ${i + 1}` })}
                                        onError={(e) => { e.target.style.opacity = '0.3'; }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Paperwork photos */}
                    {completion.paperwork_images?.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <span style={label}>Paperwork Photos ({completion.paperwork_images.length})</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                {completion.paperwork_images.map((url, i) => (
                                    <img key={i} src={url} alt={`Paperwork ${i + 1}`}
                                        style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: `1px solid ${isDark ? '#555' : '#ccc'}`, cursor: 'pointer' }}
                                        onClick={() => setEnlargedImg({ url, title: `Paperwork ${i + 1}` })}
                                        onError={(e) => { e.target.style.opacity = '0.3'; }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Enlarged image overlay */}
            {enlargedImg && (
                <div style={{ ...overlay, zIndex: 10001 }} onClick={() => setEnlargedImg(null)}>
                    <div style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <p style={{ color: '#fff', marginBottom: 8, fontSize: 14 }}>{enlargedImg.title}</p>
                        <img src={enlargedImg.url} alt={enlargedImg.title}
                            style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.6)' }}
                        />
                        <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: 8, fontSize: 12 }}>Click anywhere to close</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default RouteCompletionViewModal;
